import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useCandidateQuery } from "@/queries/candidate/candidate";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  candidateFundraisingFormSchema,
  CandidateFundraisingFormSchema,
} from "./schema";

import { useUpdateCandidateProfile } from "@/mutations/candidate/profile/updateProfile";

const CandidateFundraisingEditFormDialog = ({
  profileId,
}: {
  profileId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { candidate } = useCandidateQuery(profileId);
  const { updateCandidateProfile, isUpdatingCandidateProfile } =
    useUpdateCandidateProfile(profileId, () => setIsOpen(false));

  const form = useForm<CandidateFundraisingFormSchema>({
    resolver: zodResolver(candidateFundraisingFormSchema),
    mode: "onChange",
    defaultValues: {
      hasFundraisingExperience: candidate?.hasFundraisingExperience,
    },
  });

  useEffect(() => {
    form.reset({
      hasFundraisingExperience: candidate?.hasFundraisingExperience,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);

  const onSubmit = (data: CandidateFundraisingFormSchema) => {
    updateCandidateProfile({
      emails: candidate?.emails ?? [],
      ...data,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="edit-text self-end">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit Fundraising Experience</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Update your fundraising experience below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="hasFundraisingExperience"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>Previous Fundraising Experience</FormLabel>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(state) => {
                          field.onChange(
                            typeof state === "string" ? false : state,
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid || isUpdatingCandidateProfile
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateFundraisingEditFormDialog;
